// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@awc/MobiusOverrides/LazyImage";
import { getSystemColorsAsDropdownOptions } from "@awc/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { shallowEqual } from "react-redux";

const enum fields {
    imageLinks = "imageLinks",
    hideOnMobile = "hideOnMobile",
    color = "color",
}

export interface ImageLinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        title: string;
        image: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageLinks]: ImageLinkModel[];
        [fields.hideOnMobile]: boolean;
        [fields.color]: string;
    };
}

export interface ValueAddedServiceSidebarStyles {
    actionClickable?: ClickablePresentationProps;
    title?: TypographyPresentationProps;
    image?: LazyImageProps;
    wrapper?: InjectableCss;
}

export const valueAddedServiceSidebarStyles: ValueAddedServiceSidebarStyles = {};

const styles = valueAddedServiceSidebarStyles;

export const ValueAddedServiceSidebar: React.FC<OwnProps> = ({ fields }) => {
    const { links: imageLinks } = useGetLinks(fields.imageLinks, o => o.fields.destination);

    const { isMobileScreen } = useAppSelector(
        state => ({
            isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        }),
        shallowEqual,
    );

    if (!fields.imageLinks.length || (fields.hideOnMobile && isMobileScreen)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {imageLinks.map((link, index) => {
                const imageLink = fields.imageLinks[index].fields;
                const shouldOpenInNewWindow = imageLink.openInNewWindow;
                const hasValidUrl = Boolean(link.url);

                if (!hasValidUrl) {
                    return null;
                }

                return (
                    <Clickable
                        {...styles.actionClickable}
                        key={`${link.url}.${link.title}`}
                        href={link.url}
                        target={shouldOpenInNewWindow ? "_blank" : ""}
                        {...{ selectedColor: fields.color }}
                    >
                        <LazyImage {...styles.image} src={imageLink.image} />
                        {imageLink.title && <Typography {...styles.title}>{imageLink.title}</Typography>}
                    </Clickable>
                );
            })}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: ValueAddedServiceSidebar,
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.hideOnMobile,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.color,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#002169",
                fieldType: "General",
                tab: basicTab,
                isRequired: true,
            },
            {
                name: fields.imageLinks,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title || "title";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        displayName: "Page Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
